import {isVisible} from "../../../../default/global/js/utilities/visible";
import {fadeOut} from "../../../../default/global/js/utilities/fade";

const SEARCH_DEBOUNCE_MS = 150;

// Per dropdown index of its entries, rebuilt on every initializeCustomSearchRadio() call and
// shared with the already bound handlers.
const dropdownStates = new WeakMap();

/**
 * Wires up the searchable radio dropdowns (`.js-customSearchRadio`).
 *
 * These are also used for the individualization color pickers, which hold several thousand
 * entries. Everything here is therefore built to touch each entry at most once per interaction:
 * the searchable text is indexed up front instead of being re-read from the DOM on every
 * keystroke, item clicks run through a single delegated listener instead of one listener per
 * entry, and no loop over the entries may read layout (offsetWidth, getBoundingClientRect,
 * getComputedStyle, elementFromPoint) — interleaving those with class or style writes forces a
 * synchronous style/layout recalc per entry and made picking a color take ~20s on mobile.
 */
export function initializeCustomSearchRadio() {
    const syncDropdowns = document.querySelectorAll(".js-customSearchRadio");

    syncDropdowns.forEach(function (dropdown) {
        const hiddenSelect = dropdown.querySelector("select.customSelect__hiddenFormField");
        const selectedField = dropdown.querySelector(".customSelect__selected");
        const searchInput = dropdown.querySelector(".js-searchInput");
        const dropdownWrapper = dropdown.querySelector(".customSelect__dropdown");
        const dropdownClose = dropdown.querySelector(".customSelect__dropdownClose");

        if (!hiddenSelect || !selectedField || !dropdownWrapper) {
            return;
        }

        const entries = Array.from(dropdown.querySelectorAll(".customSelect__dropdownItem")).map(item => {
            const headline = item.querySelector(".customSelect__dropdownItemHeadline");
            const text = headline ? headline.textContent.trim() : "";

            return {
                item,
                itemId: headline?.getAttribute("data-option-item_id") || "",
                itemCode: headline?.getAttribute("data-option-code") || "",
                optionValue: headline?.getAttribute("data-option-value") || "",
                text,
                searchText: text.toLowerCase(),
                visible: item.style.display !== "none"
            };
        });

        dropdownStates.set(dropdown, {entries});

        syncActiveItem(dropdown);
        adjustFirstVisibleItemStyles(dropdown);

        // initializeCustomSearchRadio() runs repeatedly over the same nodes — the itemcard
        // MutationObserver and initButtonsForIndividualization() both call it. Bind exactly once.
        // The previous removeEventListener() guard never worked: handleDropdownItemClick was a
        // fresh closure on every call, so removing it was a no-op and every entry ended up with
        // the click handler bound twice, doing all of the work below twice per pick.
        if (dropdown.dataset.searchRadioBound === "1") {
            return;
        }
        dropdown.dataset.searchRadioBound = "1";

        if (searchInput) {
            let searchTimer = null;

            searchInput.addEventListener("input", function () {
                clearTimeout(searchTimer);
                searchTimer = setTimeout(() => applyFilter(dropdown, searchInput.value), SEARCH_DEBOUNCE_MS);
            });

            searchInput.addEventListener("click", function (event) {
                event.stopPropagation();
            });
        }

        dropdownWrapper.addEventListener("click", function (event) {
            event.stopPropagation();
        });

        if (dropdownClose) {
            dropdownClose.addEventListener('click', () => {
                dropdown.classList.remove('is-active');
                if (isVisible(document.getElementById("overlay"))) {
                    fadeOut(document.getElementById("overlay"));
                }
            });
        }

        dropdownWrapper.addEventListener("click", function (event) {
            const item = event.target.closest(".customSelect__dropdownItem");
            if (!item || !dropdownWrapper.contains(item)) {
                return;
            }

            const headline = item.querySelector(".customSelect__dropdownItemHeadline");
            const itemId = headline?.getAttribute("data-option-item_id");

            // Group headings carry no item_id and are not selectable.
            if (!itemId) {
                return;
            }

            const itemCode = headline.getAttribute("data-option-code");
            const correspondingOption = itemCode
                ? hiddenSelect.querySelector(`option[item_id="${itemId}"][code="${itemCode}"]`)
                : hiddenSelect.querySelector(`option[item_id="${itemId}"]`);

            if (correspondingOption) {
                hiddenSelect.value = correspondingOption.value;
                // Only the previously marked option needs clearing — iterating every option
                // allocated an array of thousands of nodes on each pick.
                hiddenSelect.querySelector("option[selected]")?.removeAttribute("selected");
                correspondingOption.setAttribute("selected", "selected");
            }

            selectedField.textContent = headline.textContent.trim();
            dropdown.classList.remove("is-active");
            if (isVisible(document.getElementById("overlay"))) {
                fadeOut(document.getElementById("overlay"));
            }

            if (searchInput) {
                searchInput.value = "";
                applyFilter(dropdown, "");
            }

            hiddenSelect.dispatchEvent(new Event("change", {bubbles: true}));

            syncActiveItem(dropdown);
        });
    });
}

/**
 * Shows the entries matching the query and hides the rest. Only entries whose visibility
 * actually changes are written to, so narrowing a search touches a handful of nodes instead
 * of all of them.
 */
function applyFilter(dropdown, query) {
    const state = dropdownStates.get(dropdown);
    if (!state) {
        return;
    }

    const searchQuery = query.toLowerCase().trim();

    state.entries.forEach(entry => {
        const visible = entry.searchText.includes(searchQuery);
        if (entry.visible !== visible) {
            entry.visible = visible;
            entry.item.style.display = visible ? "" : "none";
        }
    });

    adjustFirstVisibleItemStyles(dropdown);
}

/**
 * Drops the separator above the first visible entry when that entry is a group heading.
 */
function adjustFirstVisibleItemStyles(dropdown) {
    const state = dropdownStates.get(dropdown);
    if (!state) {
        return;
    }

    let firstVisible = null;

    state.entries.forEach(entry => {
        if (entry.visible && entry.text !== "") {
            if (!firstVisible) {
                firstVisible = entry;
            }
            entry.item.style.borderTop = "";
            entry.item.style.paddingTop = "";
        }
    });

    if (firstVisible && firstVisible.optionValue.trim() === "") {
        firstVisible.item.style.borderTop = "none";
        firstVisible.item.style.paddingTop = "0";
    }
}

/**
 * Highlights the entry belonging to the currently selected option.
 *
 * Matches on the selected option's item_id/code the same way the click handler resolves an entry
 * to its option — the code only counts when the entry carries one. Comparing data-option-item_id
 * to hiddenSelect.value (as this did before) only worked where item_id equals the option value;
 * in the individualization dropdowns the value is the item_no, so nothing ever matched and the
 * chosen color stayed unhighlighted when the dropdown was reopened.
 */
function syncActiveItem(dropdown) {
    const state = dropdownStates.get(dropdown);
    const hiddenSelect = dropdown.querySelector("select.customSelect__hiddenFormField");
    if (!state || !hiddenSelect) {
        return;
    }

    const selectedOption = hiddenSelect.selectedIndex >= 0
        ? hiddenSelect.options[hiddenSelect.selectedIndex]
        : null;
    const selectedItemId = selectedOption?.getAttribute("item_id") || "";
    const selectedCode = selectedOption?.getAttribute("code") || "";

    state.entries.forEach(entry => {
        const isActive = entry.itemId !== ""
            && entry.itemId === selectedItemId
            && (!entry.itemCode || entry.itemCode === selectedCode);

        entry.item.classList.toggle("is-active", isActive);
    });
}
